import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import useAlertBanner from '#/shared/hooks/useAlertBanner';
import * as Styled from './styled';

function TripToolTabs({ tabConfig, onTabClick, sidePanelIsMinimized }) {
  const { alertBannerIsVisible, globalAlertTimestamp } = useSelector(
    s => s.user
  );
  const { dismissBanner } = useAlertBanner();
  const intl = useIntl();

  function tripToolTipHandler(tab) {
    return intl.formatMessage({
      id: `triptooltabs.${
        {
          tracker: 'tracker-tooltip',
          planner: 'planner-tooltip',
          routes: 'routes-tooltip',
          alerts: 'alerts-tooltip',
        }[tab]
      }`,
    });
  }

  return (
    <Styled.TabList
      aria-label={intl.formatMessage({ id: 'triptooltabs.tablist' })}
      minimized={sidePanelIsMinimized}
    >
      {tabConfig.map(t => {
        return (
          <Styled.Tab
            to={t.route.navigate()}
            aria-current={t.active ? 'page' : undefined}
            key={t.id}
            tab={t.id}
            onClick={() => {
              onTabClick(t);
              if (t.id === 'alerts') {
                dismissBanner();
              }
            }}
            data-gtm={t.dataGtm}
            title={tripToolTipHandler(t.id)}
          >
            <Styled.TabIconRow aria-hidden="true">
              <Styled.TripToolIcon type={t.id} $active={t.active} />
              {!alertBannerIsVisible &&
                globalAlertTimestamp &&
                t.id === 'alerts' && <Styled.AlertNotificationDot />}
            </Styled.TabIconRow>
            <Styled.TabName>
              <FormattedMessage id={`triptooltabs.${t.id}`} />
            </Styled.TabName>
          </Styled.Tab>
        );
      })}
    </Styled.TabList>
  );
}

TripToolTabs.propTypes = {
  /** Configuration for tab elements that this component generates */
  tabConfig: PropTypes.arrayOf(
    PropTypes.shape({
      /** Tab ID, used as key and for i18n and icon lookup */
      id: PropTypes.string.isRequired,
      /** Object containing information about an application route */
      route: PropTypes.shape({
        /** Function that when invoked returns a navigable path for a route */
        navigate: PropTypes.func.isRequired,
      }).isRequired,
      /** Whether the tag is active, affects styling */
      active: PropTypes.bool.isRequired,
      /** Google Tag Manager value added as DOM attribute */
      dataGtm: PropTypes.string.isRequired,
    })
  ).isRequired,
  /** Handler fired when any tab is clicked, passed config for clicked tab */
  onTabClick: PropTypes.func,
  /** True if the side panel is minimized */
  sidePanelIsMinimized: PropTypes.bool,
};

TripToolTabs.defaultProps = {
  sidePanelIsMinimized: false,
  onTabClick: () => {},
};

export default TripToolTabs;
